import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Loader2, Pencil, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { InlineMicButton } from '../components/InlineMicButton';
import type { ProcedureTemplate } from '../types/clinical';

const ANAESTHESIA_OPTIONS = ['General', 'Spinal', 'Saddle', 'Conscious Sedation', 'Epidural', 'Other'];

/** Convert a comma-separated string to a trimmed string array, filtering blanks. */
function csvToArray(csv: string): string[] {
  return csv
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

/** Convert a string array to a comma-separated string. */
function arrayToCsv(arr: string[] | undefined): string {
  return (arr ?? []).join(', ');
}

interface FormState {
  templateType: 'surgery' | 'consultation';
  name: string;
  category: string;
  // Surgery fields
  anaesthesiaType: string;
  procedureNotes: string;
  postOpInstructions: string;
  icd10Codes: string; // comma-separated
  // Consultation fields
  assessment: string;
  plan: string;
  summary: string;
  followUp: string;
  consultIcd10Codes: string; // comma-separated
  nhrplCodes: string; // comma-separated
}

const EMPTY_FORM: FormState = {
  templateType: 'surgery',
  name: '',
  category: '',
  anaesthesiaType: '',
  procedureNotes: '',
  postOpInstructions: '',
  icd10Codes: '',
  assessment: '',
  plan: '',
  summary: '',
  followUp: '',
  consultIcd10Codes: '',
  nhrplCodes: '',
};

export function ProcedureTemplatesPage() {
  const { get, post, patch } = useApi();
  const { userRole } = useAuth();
  const isDoctor = userRole === 'doctor';

  const [templates, setTemplates] = useState<ProcedureTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ProcedureTemplate | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const updateForm = (p: Partial<FormState>) => setForm(prev => {
    const next = { ...prev, ...p };
    // Auto-save to localStorage
    try { localStorage.setItem('ikhan_template_draft', JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  // Restore draft from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('ikhan_template_draft');
      if (saved) {
        const parsed = JSON.parse(saved) as FormState;
        if (parsed.name) {
          setForm(parsed);
          setShowModal(true);
        }
      }
    } catch { /* ignore */ }
  }, []);

  // Clear draft after successful save
  const clearDraft = () => { try { localStorage.removeItem('ikhan_template_draft'); } catch { /* ignore */ } };

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get<ProcedureTemplate[]>('/procedure-templates?include_inactive=true');
      if (r.success && r.data) setTemplates(r.data);
      else setError(r.error || 'Failed to load procedure templates');
    } catch {
      setError('Failed to load procedure templates');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openAddModal = () => {
    setEditingTemplate(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEditModal = (t: ProcedureTemplate) => {
    setEditingTemplate(t);
    setForm({
      templateType: t.templateType || 'surgery',
      name: t.name,
      category: t.category,
      anaesthesiaType: t.anaesthesiaType || '',
      procedureNotes: t.procedureNotes || '',
      postOpInstructions: t.postOpInstructions || '',
      icd10Codes: arrayToCsv(t.defaultIcd10Codes),
      assessment: t.assessment || '',
      plan: t.plan || '',
      summary: t.summary || '',
      followUp: t.followUp || '',
      consultIcd10Codes: t.templateType === 'consultation' ? arrayToCsv(t.defaultIcd10Codes) : '',
      nhrplCodes: arrayToCsv(t.defaultNhrplCodes),
    });
    setShowModal(true);
  };

  /** Build the payload from form state. */
  const buildPayload = () => {
    const base = {
      name: form.name.trim(),
      category: form.category.trim(),
      templateType: form.templateType,
    };

    if (form.templateType === 'surgery') {
      return {
        ...base,
        anaesthesiaType: form.anaesthesiaType,
        procedureNotes: form.procedureNotes,
        postOpInstructions: form.postOpInstructions,
        defaultIcd10Codes: csvToArray(form.icd10Codes),
        // Clear consultation fields
        assessment: '',
        plan: '',
        summary: '',
        followUp: '',
        defaultNhrplCodes: [],
        content: form.procedureNotes, // legacy compat
      };
    } else {
      return {
        ...base,
        assessment: form.assessment,
        plan: form.plan,
        summary: form.summary,
        followUp: form.followUp,
        defaultIcd10Codes: csvToArray(form.consultIcd10Codes),
        defaultNhrplCodes: csvToArray(form.nhrplCodes),
        // Clear surgery fields
        anaesthesiaType: '',
        procedureNotes: '',
        postOpInstructions: '',
        content: form.plan, // legacy compat
      };
    }
  };

  /** True when the form has enough data to save. */
  const canSave = () => {
    if (!form.name.trim()) return false;
    if (form.templateType === 'surgery') return !!form.procedureNotes.trim();
    return !!form.assessment.trim() || !!form.plan.trim();
  };

  const handleSave = async () => {
    if (!canSave()) return;
    setSaving(true);
    const payload = buildPayload();

    if (editingTemplate) {
      const r = await patch<ProcedureTemplate>(`/procedure-templates/${editingTemplate.id}`, payload);
      if (r.success) {
        await fetchTemplates();
      } else {
        setError(r.error || 'Failed to update template');
        setSaving(false);
        return;
      }
    } else {
      const r = await post<ProcedureTemplate>('/procedure-templates', payload);
      if (r.success) {
        await fetchTemplates();
      } else {
        setError(r.error || 'Failed to create template');
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    setShowModal(false);
    clearDraft();
  };

  const handleToggleActive = async (id: string, active: boolean) => {
    const r = await patch<ProcedureTemplate>(`/procedure-templates/${id}`, { active });
    if (r.success) await fetchTemplates();
    else setError(r.error || `Failed to ${active ? 'reactivate' : 'deactivate'} template`);
  };

  if (!isDoctor) {
    return (
      <main className="max-w-4xl mx-auto px-4 py-8">
        <p className="text-gray-500">This page is only accessible to doctors.</p>
      </main>
    );
  }

  const filtered = showInactive ? templates : templates.filter(t => t.active);

  /** Get a preview string for a template card. */
  const getPreview = (t: ProcedureTemplate): string => {
    const text = t.templateType === 'consultation'
      ? (t.assessment || t.plan || '')
      : (t.procedureNotes || t.content || '');
    return text.length > 100 ? text.slice(0, 100) + '...' : text;
  };

  return (
    <main className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileText className="h-6 w-6 text-teal-600" />
          <h1 className="text-2xl font-bold text-gray-900">Procedure Templates</h1>
        </div>
        <button
          onClick={openAddModal}
          className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-1"
        >
          <Plus className="h-4 w-4" /> New Template
        </button>
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="mb-4">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
            className="rounded border-gray-300"
          />
          Show inactive
        </label>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">No templates created yet.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map(t => (
            <div key={t.id} className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${!t.active ? 'opacity-60' : ''}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">{t.name}</span>
                  {t.category && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{t.category}</span>
                  )}
                  {/* Type badge */}
                  {t.templateType === 'consultation' ? (
                    <span className="text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded font-medium">Consultation</span>
                  ) : (
                    <span className="text-xs text-teal-700 bg-teal-50 px-2 py-0.5 rounded font-medium">Surgery</span>
                  )}
                  {!t.active && (
                    <span className="text-xs text-red-500 bg-red-50 px-1.5 py-0.5 rounded">Inactive</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditModal(t)}
                    className="p-1 text-gray-400 hover:text-blue-600 rounded"
                    title="Edit template"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  {t.active ? (
                    <button
                      onClick={() => handleToggleActive(t.id, false)}
                      className="p-1 text-gray-400 hover:text-red-500 rounded"
                      title="Deactivate template"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleToggleActive(t.id, true)}
                      className="text-xs text-green-600 hover:text-green-800 flex items-center gap-0.5"
                    >
                      <RotateCcw className="h-3 w-3" /> Reactivate
                    </button>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600 whitespace-pre-wrap line-clamp-2">{getPreview(t)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">{editingTemplate ? 'Edit Template' : 'New Template'}</h2>
              <button onClick={() => { setShowModal(false); clearDraft(); }} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Template Type Toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template Type</label>
                <div className="flex rounded-md overflow-hidden border border-gray-300 w-fit">
                  <button
                    type="button"
                    onClick={() => updateForm({ templateType: 'surgery' })}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      form.templateType === 'surgery'
                        ? 'bg-teal-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Surgery
                  </button>
                  <button
                    type="button"
                    onClick={() => updateForm({ templateType: 'consultation' })}
                    className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-300 ${
                      form.templateType === 'consultation'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Consultation
                  </button>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                <input
                  autoFocus
                  value={form.name}
                  onChange={e => updateForm({ name: e.target.value })}
                  placeholder="e.g. Caesarean Section, Hysteroscopy"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={form.category}
                  onChange={e => updateForm({ category: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">— Select —</option>
                  <option value="Obstetrics">Obstetrics</option>
                  <option value="Gynaecology">Gynaecology</option>
                </select>
              </div>

              {/* ---- Surgery Fields ---- */}
              {form.templateType === 'surgery' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Anaesthesia Type</label>
                    <select
                      value={form.anaesthesiaType}
                      onChange={e => updateForm({ anaesthesiaType: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="">-- Select --</option>
                      {ANAESTHESIA_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Procedure Notes *</label>
                      <InlineMicButton onTranscript={t => updateForm({ procedureNotes: form.procedureNotes ? form.procedureNotes + ' ' + t : t })} />
                    </div>
                    <textarea
                      value={form.procedureNotes}
                      onChange={e => updateForm({ procedureNotes: e.target.value })}
                      placeholder="Default procedure notes for this template..."
                      rows={6}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Post-Op Instructions</label>
                      <InlineMicButton onTranscript={t => updateForm({ postOpInstructions: form.postOpInstructions ? form.postOpInstructions + ' ' + t : t })} />
                    </div>
                    <textarea
                      value={form.postOpInstructions}
                      onChange={e => updateForm({ postOpInstructions: e.target.value })}
                      placeholder="Default post-operative instructions..."
                      rows={6}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ICD-10 Codes</label>
                    <input
                      value={form.icd10Codes}
                      onChange={e => updateForm({ icd10Codes: e.target.value })}
                      placeholder="e.g. N80.1, Z87.3"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Comma-separated default ICD-10 codes</p>
                  </div>
                </>
              )}

              {/* ---- Consultation Fields ---- */}
              {form.templateType === 'consultation' && (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Assessment *</label>
                      <InlineMicButton onTranscript={t => updateForm({ assessment: form.assessment ? form.assessment + ' ' + t : t })} />
                    </div>
                    <textarea
                      value={form.assessment}
                      onChange={e => updateForm({ assessment: e.target.value })}
                      placeholder="Default assessment text..."
                      rows={4}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Plan</label>
                      <InlineMicButton onTranscript={t => updateForm({ plan: form.plan ? form.plan + ' ' + t : t })} />
                    </div>
                    <textarea
                      value={form.plan}
                      onChange={e => updateForm({ plan: e.target.value })}
                      placeholder="Default plan text..."
                      rows={6}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Summary</label>
                      <InlineMicButton onTranscript={t => updateForm({ summary: form.summary ? form.summary + ' ' + t : t })} />
                    </div>
                    <textarea
                      value={form.summary}
                      onChange={e => updateForm({ summary: e.target.value })}
                      placeholder="Brief summary..."
                      rows={2}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Follow-up</label>
                      <InlineMicButton onTranscript={t => updateForm({ followUp: form.followUp ? form.followUp + ' ' + t : t })} />
                    </div>
                    <input
                      value={form.followUp}
                      onChange={e => updateForm({ followUp: e.target.value })}
                      placeholder="e.g. 4 weeks"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ICD-10 Codes</label>
                    <input
                      value={form.consultIcd10Codes}
                      onChange={e => updateForm({ consultIcd10Codes: e.target.value })}
                      placeholder="e.g. N80.1, Z87.3"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Comma-separated default ICD-10 codes</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">NHRPL Codes</label>
                    <input
                      value={form.nhrplCodes}
                      onChange={e => updateForm({ nhrplCodes: e.target.value })}
                      placeholder="e.g. 0076, 0190"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Comma-separated default NHRPL procedure codes</p>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t">
              <button onClick={() => { setShowModal(false); clearDraft(); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving || !canSave()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editingTemplate ? 'Save Changes' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
